
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthHandler } from "@/hooks/useAuthHandler";
import { useAuthForm } from "@/context/auth-form";

export const useForgotPasswordForm = () => {
  const { email, setEmail } = useAuthForm();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const { handlePasswordReset } = useAuthHandler();
  const navigate = useNavigate();

  // Clear error message when email changes
  useEffect(() => {
    if (errorMessage) {
      setErrorMessage("");
    }
  }, [email]);

  // Add document-level click handler to reset success state when clicking anywhere
  useEffect(() => {
    const handleDocumentClick = () => {
      if (success) {
        setSuccess(false);
      }
    };

    // Add event listener to the document
    document.addEventListener("click", handleDocumentClick);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [success]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    
    if (!email) {
      setErrorMessage("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }
    
    setLoading(true);
    setSuccess(false);
    
    try {
      const result = await handlePasswordReset(email);
      
      if (result.success) {
        setSuccess(true);
        // No toast notification here - we're showing success through UI state
      } else {
        setErrorMessage(result.error || "Failed to send reset link");
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      setErrorMessage(error.message || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  // Handler for back to login with proper navigation
  const handleBackToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/auth?mode=login", { replace: true });
  };

  // Handler to reset the form state when clicked - separate from document click
  // to prevent event propagation issues
  const handleFormClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent double-handling with document click
    e.stopPropagation();
  };

  return {
    email,
    setEmail,
    loading,
    errorMessage,
    success,
    handleSubmit,
    handleBackToLogin,
    handleFormClick
  };
};
